import _ from 'lodash';

import { adminClient as apolloClient } from '@/store/apollo';

import UserGql from '@/store/graphql/system/queries/User.gql';
import UsersPaginatedGql from '@/store/graphql/system/queries/UsersPaginated.gql';
// import OwnersPaginatedGql from '@/store/graphql/system/queries/OwnersPaginated.gql';
import CreateUserGql from '@/store/graphql/system/mutations/CreateUser.gql';
import UpdateUserGql from '@/store/graphql/system/mutations/UpdateUser.gql';
import DeleteUserGql from '@/store/graphql/system/mutations/DeleteUser.gql';

const germanSearchFilterKeys = [
    'company',
    'name',
    'first_name',
    'last_name',
    'address_street',
    'address_city',
    'address_country'
];

const expandedToUmlaut = {
    a: '\u00e4',
    A: '\u00c4',
    o: '\u00f6',
    O: '\u00d6',
    u: '\u00fc',
    U: '\u00dc'
};

const umlautToExpanded = {
    '\u00c4': 'Ae',
    '\u00d6': 'Oe',
    '\u00dc': 'Ue',
    '\u00e4': 'ae',
    '\u00f6': 'oe',
    '\u00fc': 'ue',
    '\u00df': 'ss'
};

const collapseGermanUmlautSpelling = value =>
    value.replace(/(^|[^aeiou\u00e4\u00f6\u00fcAEIOU\u00c4\u00d6\u00dc])([aouAOU])e/gi, (match, prefix, vowel) => {
        return `${prefix}${expandedToUmlaut[vowel] || vowel}`;
    });

const expandGermanUmlauts = value =>
    value.replace(/[\u00c4\u00d6\u00dc\u00e4\u00f6\u00fc\u00df]/g, match => umlautToExpanded[match] || match);

const getGermanSearchFallbackValue = value => {
    const collapsed = collapseGermanUmlautSpelling(value);
    if (collapsed !== value) return collapsed;

    const expanded = expandGermanUmlauts(value);
    if (expanded !== value) return expanded;

    return value;
};

const createGermanSearchFallback = data => {
    const fallback = _.cloneDeep(data);
    let hasFallback = false;

    _.each(fallback.filters, filter => {
        if (!germanSearchFilterKeys.includes(filter.key) || !_.isString(filter.like) || _.isEmpty(filter.like)) {
            return;
        }

        const fallbackValue = getGermanSearchFallbackValue(filter.like);
        if (fallbackValue === filter.like) return;

        filter.like = fallbackValue;
        hasFallback = true;
    });

    return hasFallback ? fallback : null;
};

const fetchUsersPaginatedRequest = data => {
    let filters = [];
    _.each(data.filters, entry => filters.push(entry));

    return apolloClient
        .query({
            query: UsersPaginatedGql,
            variables: {
                scope: _.get(data, 'scope', 'base'),
                type: _.get(data, 'type', 'user'),
                currentPage: _.get(data, 'pageInfo.currentPage', 1),
                perPage: _.get(data, 'pageInfo.perPage', 25),
                order: _.get(data, 'variables.order', 'id'),
                direction: _.get(data, 'variables.direction', 'asc'),
                // link: _.get(data, 'link.route', '/sales/order/'),
                filters
            }
        })
        .then(({ data }) => data.UsersPaginated);
};

const fetchUsersPaginatedWithFallback = data => {
    return fetchUsersPaginatedRequest(data).then(result => {
        const fallback = createGermanSearchFallback(data);
        if (!fallback || _.get(result, 'pageInfo.total', 0) > 0) return result;

        return fetchUsersPaginatedRequest(fallback);
    });
};

export default {
    fetchUsersPaginated: data =>
        new Promise((resolve, reject) => {
            fetchUsersPaginatedWithFallback(data)
                .then(result => resolve(result))
                .catch(err => reject(err));
        }),
    // fetchOwnersPaginated: data =>
    //     new Promise((resolve, reject) => {
    //         let filters = [];
    //         _.each(data.filters, entry => filters.push(entry));

    //         apolloClient
    //             .query({
    //                 query: OwnersPaginatedGql,
    //                 variables: {
    //                     scope: _.get(data, 'scope', 'base'),
    //                     type: _.get(data, 'type', 'owner'),
    //                     currentPage: _.get(data, 'pageInfo.currentPage', 1),
    //                     perPage: _.get(data, 'pageInfo.perPage', 25),
    //                     order: _.get(data, 'variables.order', 'updated_at'),
    //                     direction: _.get(data, 'variables.direction', 'desc'),
    //                     // link: _.get(data, 'link.route', '/sales/order/'),
    //                     filters
    //                 }
    //             })
    //             .then(({ data }) => resolve(data.OwnersPaginated))
    //             .catch(err => reject(err));
    //     }),
    fetchUser: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: UserGql,
                    variables: { id: parseInt(id) },
                    fetchPolicy: 'no-cache'
                })
                .then(({ data }) => resolve(data.User))
                .catch(err => reject(err));
        });
    },
    create: user => {
        if (user === undefined) throw new Error('required argument is missing');
        user = _.removeDeepByKey(user, '__typename');
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateUserGql,
                    variables: { user }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CreateUser);
                })
                .catch(err => reject(err));
        });
    },
    update: user => {
        if (user === undefined) throw new Error('required argument is missing');

        user = _.removeDeepByKey(user, '__typename');
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateUserGql,
                    variables: { user }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.UpdateUser);
                })
                .catch(err => reject(err));
        });
    },
    delete: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteUserGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_DeleteUser);
                })
                .catch(err => reject(err));
        });
    }
    // TODO
    // acceptTeamInvitation: token => {
    //     if (token === undefined) throw new Error('required argument is missing');

    //     return new Promise((resolve, reject) => {
    //         apolloClient
    //             .mutate({
    //                 mutation: AcceptTeamInvitationGql,
    //                 variables: {token}
    //             })
    //             .then(({data}) => {
    //                 console.log(data);
    //                 // apolloClient.clearStore();
    //                 // resolve(data.CO_DeleteUser);
    //             })
    //             .catch(err => reject(err));
    //     });
    // }
};
